import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { listProspects } from 'src/supportPortal/services/patientProspectsApi'
import PromoteProspectDialog from './PromoteProspectDialog'
import RejectProspectDialog from './RejectProspectDialog'
import ProspectDetailDrawer from './ProspectDetailDrawer'
import ValidatePatientInfoDialog from './ValidatePatientInfoDialog'

const STATUS_OPTIONS = [
  { id: '', label: 'All statuses' },
  { id: 'SUBMITTED', label: 'Submitted', color: 'primary' },
  { id: 'IN_TRIAGE', label: 'In triage', color: 'info' },
  { id: 'AWAITING_BOOKING', label: 'Awaiting booking', color: 'warning' },
  { id: 'PROMOTED', label: 'Promoted', color: 'success' },
  { id: 'CLOSED', label: 'Closed', color: 'default' },
]

const PATHWAY_OPTIONS = [
  { id: '', label: 'All pathways' },
  { id: 'PSYCHIATRY', label: 'Psychiatry' },
  { id: 'THERAPY', label: 'Therapy / Psychology' },
  { id: 'BIOLOGICAL', label: 'Biological (endocrinology / rheumatology)' },
  { id: 'CHILD', label: 'Child growth' },
  { id: 'ASSESSMENT', label: 'Not sure (assessment)' },
]

const PAGE_SIZE = 20

function statusChip(status) {
  const opt = STATUS_OPTIONS.find((s) => s.id === status)
  if (!opt) return <Chip size="small" label={status || '—'} />
  return <Chip size="small" label={opt.label} color={opt.color || 'default'} variant={opt.color ? 'filled' : 'outlined'} />
}

function timeAgo(iso) {
  if (!iso) return '—'
  const t = new Date(iso).getTime()
  if (!Number.isFinite(t)) return '—'
  const diff = Date.now() - t
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return new Date(iso).toLocaleDateString()
}

/**
 * Patient Prospects queue for the support portal.
 *
 * Shows a paginated, filterable table of {@link PatientProspectInfo} rows
 * with row actions to promote (manual brand assignment) or close (soft
 * reject). Click a row to open the detail drawer (Phase 4 follow-up).
 *
 * Backend: /api/secure/cc/support/patient-prospect/list
 * Plan reference: docs/PATIENT_PROSPECT_SIGNUP_PLAN.md §7
 */
export default function PatientProspectsListPage() {
  const [items, setItems] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [statusFilter, setStatusFilter] = useState('SUBMITTED')
  const [pathwayFilter, setPathwayFilter] = useState('')
  const [search, setSearch] = useState('')
  const [committedSearch, setCommittedSearch] = useState('')

  const [promoteFor, setPromoteFor] = useState(null)
  const [rejectFor, setRejectFor] = useState(null)
  const [drawerFor, setDrawerFor] = useState(null)
  // Auto-validate prompt: opens ValidatePatientInfoDialog with the freshly-
  // created PatientInfo right after a successful Promote, so support staff
  // doesn't have to navigate to a separate patient list to clear the
  // requiresValidation flag.
  const [validateFor, setValidateFor] = useState(null)

  const fetchPage = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await listProspects({
        status: statusFilter || undefined,
        pathway: pathwayFilter || undefined,
        q: committedSearch || undefined,
        page,
        size: PAGE_SIZE,
      })
      if (res?.status >= 200 && res?.status < 300) {
        setItems(res.data?.items || [])
        setTotalCount(res.data?.totalCount || 0)
      } else {
        setError(res?.response?.data?.message || res?.data?.message || 'Could not load prospects.')
      }
    } catch (e) {
      setError(e?.message || 'Could not load prospects.')
    } finally {
      setLoading(false)
    }
  }, [statusFilter, pathwayFilter, committedSearch, page])

  useEffect(() => {
    fetchPage()
  }, [fetchPage])

  // Reset page when filters change.
  useEffect(() => {
    setPage(0)
  }, [statusFilter, pathwayFilter, committedSearch])

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalCount / PAGE_SIZE)), [totalCount])

  const onSearchSubmit = (e) => {
    e.preventDefault()
    setCommittedSearch(search.trim())
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>Patient Prospects</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Public sign-ups via /care/get-started awaiting promotion. Use the row actions to
        promote to a clinic brand or close the request.
      </Typography>

      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Pathway</InputLabel>
          <Select
            value={pathwayFilter}
            label="Pathway"
            onChange={(e) => setPathwayFilter(e.target.value)}
          >
            {PATHWAY_OPTIONS.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box component="form" onSubmit={onSearchSubmit} sx={{ display: 'flex', gap: 1, flexGrow: 1, minWidth: 240 }}>
          <TextField
            size="small"
            fullWidth
            label="Search by name or mobile"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button type="submit" variant="outlined">Search</Button>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
      ) : error ? (
        <Paper sx={{ p: 3, color: 'error.main' }}>{error}</Paper>
      ) : items.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          No prospects match these filters.
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Pathway</TableCell>
                  <TableCell>Specialty</TableCell>
                  <TableCell>For</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((p) => (
                  <TableRow
                    key={p.id}
                    hover
                    onClick={() => setDrawerFor(p)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{p.name || <em>—</em>}</TableCell>
                    <TableCell>{p.mobile}</TableCell>
                    <TableCell>{p.pathway}</TableCell>
                    <TableCell>{p.specialtyTag || '—'}</TableCell>
                    <TableCell>{p.subjectType || 'SELF'}</TableCell>
                    <TableCell>{timeAgo(p.createdAt)}</TableCell>
                    <TableCell>{statusChip(p.status)}</TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="small"
                        variant="contained"
                        sx={{ mr: 1 }}
                        disabled={p.status === 'PROMOTED' || p.status === 'CLOSED'}
                        onClick={() => setPromoteFor(p)}
                      >
                        Promote
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        disabled={p.status === 'CLOSED' || p.status === 'PROMOTED'}
                        onClick={() => setRejectFor(p)}
                      >
                        Close
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page + 1}
                onChange={(_, p) => setPage(p - 1)}
              />
            </Box>
          ) : null}
        </>
      )}

      <PromoteProspectDialog
        open={!!promoteFor}
        prospect={promoteFor}
        onClose={() => setPromoteFor(null)}
        onPromoted={(patientInfo) => {
          setPromoteFor(null)
          // If the promote was launched from inside the drawer, close it
          // too — the prospect's status has changed and the drawer should
          // re-open with fresh data only if the user wants to.
          setDrawerFor(null)
          fetchPage()
          // Chain straight into the validate prompt — the just-returned
          // PatientInfo carries requiresValidation=true and the staff
          // member is still in context to confirm what they verified.
          if (patientInfo && patientInfo.id) {
            setValidateFor(patientInfo)
          }
        }}
      />
      <RejectProspectDialog
        open={!!rejectFor}
        prospect={rejectFor}
        onClose={() => setRejectFor(null)}
        onRejected={() => {
          setRejectFor(null)
          setDrawerFor(null)
          fetchPage()
        }}
      />
      <ProspectDetailDrawer
        open={!!drawerFor}
        prospectSummary={drawerFor}
        onClose={() => setDrawerFor(null)}
        onPromote={(p) => setPromoteFor(p)}
        onReject={(p) => setRejectFor(p)}
      />
      <ValidatePatientInfoDialog
        open={!!validateFor}
        patientInfo={validateFor}
        onClose={() => setValidateFor(null)}
        onValidated={() => setValidateFor(null)}
      />
    </Box>
  )
}
